import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Fab,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
  Mail as MailIcon,
} from '@mui/icons-material';
import {
  organizationApi,
  Organization,
  OrganizationMember,
  OrganizationInvitation,
  OrganizationRole,
  OrganizationCreateRequest,
  OrganizationUpdateRequest,
  InvitationCreateRequest,
  isOwner,
  canManageMembers,
  canViewMembers,
  canViewInvitations,
  canDeleteOrganization,
  canUpdateOrganization,
} from '../services/organizationApi';

// Import organization sub-components
import OrganizationOverview from './organization/OrganizationOverview';
import MemberManagement from './organization/MemberManagement';
import InvitationManagement from './organization/InvitationManagement';
import OrganizationSettings from './organization/OrganizationSettings';
import PendingInvitations from './PendingInvitations';

// Import dialog components
import CreateOrganizationDialog from './organization/CreateOrganizationDialog';
import EditOrganizationDialog from './organization/EditOrganizationDialog';
import DeleteOrganizationDialog from './organization/DeleteOrganizationDialog';
import InviteMemberDialog from './organization/InviteMemberDialog';
import UpdateMemberRoleDialog from './organization/UpdateMemberRoleDialog';
import TransferOwnershipDialog from './organization/TransferOwnershipDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`org-tabpanel-${index}`}
      aria-labelledby={`org-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function OrganizationManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [myInvitations, setMyInvitations] = useState<OrganizationInvitation[]>([]);
  const [invitationsLastUpdated, setInvitationsLastUpdated] = useState<Date | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [roleUpdateDialogOpen, setRoleUpdateDialogOpen] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState<OrganizationCreateRequest>({
    name: '',
    description: '',
    domain: '',
    industry: '',
    size: '',
  });
  const [editForm, setEditForm] = useState<OrganizationUpdateRequest>({});
  const [inviteForm, setInviteForm] = useState<InvitationCreateRequest>({
    email: '',
    role: OrganizationRole.MEMBER,  // Role they will have after accepting
  });
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);
  const [newRole, setNewRole] = useState<OrganizationRole | string | number>(OrganizationRole.MEMBER);
  const [newOwnerId, setNewOwnerId] = useState('');
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadOrganizationDetails = useCallback(async (orgId: string) => {
    try {
      // Always fetch members (regular members can see other members)
      const membersData = await organizationApi.getOrganizationMembers(orgId);
      setMembers(membersData);
      
      // Only fetch invitations if user has permission to view them
      if (selectedOrg && selectedOrg.user_role && canViewInvitations(selectedOrg.user_role)) {
        try {
          const invitationsData = await organizationApi.getOrganizationInvitations(orgId);
          setInvitations(invitationsData);
        } catch (invitationError) {
          console.warn('User does not have permission to view invitations:', invitationError);
          setInvitations([]);
        }
      } else {
        setInvitations([]);
      }
    } catch (error) {
      showSnackbar('Failed to load organization details', 'error');
    }
  }, [showSnackbar, selectedOrg]);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const orgs = await organizationApi.getMyOrganizations();
      setOrganizations(orgs);
      if (orgs.length > 0 && !selectedOrg) {
        setSelectedOrg(orgs[0]);
        loadOrganizationDetails(orgs[0].id);
      }
    } catch (error) {
      showSnackbar('Failed to load organizations', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, loadOrganizationDetails, showSnackbar]);

  const loadMyInvitations = useCallback(async (showSuccess = false) => {
    try {
      setInvitationsLoading(true);
      const invitations = await organizationApi.getMyInvitations();
      setMyInvitations(invitations);
      setInvitationsLastUpdated(new Date());
      if (showSuccess) {
        showSnackbar('Invitations refreshed successfully', 'success');
      }
    } catch (error) {
      if (showSuccess) {
        showSnackbar('Failed to refresh invitations', 'error');
      }
      // Silently fail for initial load
    } finally {
      setInvitationsLoading(false);
    }
  }, [showSnackbar]);

  // Load data on component mount
  useEffect(() => {
    loadOrganizations();
    loadMyInvitations(false);
  }, [loadOrganizations, loadMyInvitations]);

  const handleCreateOrganization = async () => {
    try {
      setLoading(true);
      const newOrg = await organizationApi.createOrganization(createForm);
      setOrganizations([...organizations, newOrg]);
      setSelectedOrg(newOrg);
      setCreateDialogOpen(false);
      resetCreateForm();
      showSnackbar('Organization created successfully', 'success');
      loadOrganizationDetails(newOrg.id);
    } catch (error) {
      showSnackbar('Failed to create organization', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!selectedOrg) return;
    try {
      setLoading(true);
      const updatedOrg = await organizationApi.updateOrganization(selectedOrg.id, editForm);
      setOrganizations(orgs => orgs.map(org => org.id === selectedOrg.id ? updatedOrg : org));
      setSelectedOrg(updatedOrg);
      setEditDialogOpen(false);
      resetEditForm();
      showSnackbar('Organization updated successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to update organization', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrg) return;
    try {
      setLoading(true);
      await organizationApi.deleteOrganization(selectedOrg.id);
      setOrganizations(orgs => orgs.filter(org => org.id !== selectedOrg.id));
      setSelectedOrg(null);
      setDeleteDialogOpen(false);
      showSnackbar('Organization deleted successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to delete organization', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async () => {
    if (!selectedOrg) return;
    try {
      setLoading(true);
      const invitation = await organizationApi.createInvitation(selectedOrg.id, inviteForm);
      setInvitations([...invitations, invitation]);
      setInviteDialogOpen(false);
      resetInviteForm();
      showSnackbar('Invitation sent successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to send invitation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedOrg) return;
    try {
      setLoading(true);
      await organizationApi.removeMember(selectedOrg.id, memberId);
      setMembers(members.filter(member => member.id !== memberId));
      showSnackbar('Member removed successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to remove member', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMemberRole = async () => {
    if (!selectedOrg || !selectedMember) return;
    try {
      setLoading(true);
      await organizationApi.updateMemberRole(selectedOrg.id, selectedMember.user_id, { role: newRole });
      setMembers(members.map(member => 
        member.id === selectedMember.id ? { ...member, role: newRole } : member
      ));
      setRoleUpdateDialogOpen(false);
      showSnackbar('Member role updated successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to update member role', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedOrg) return;
    try {
      setLoading(true);
      await organizationApi.transferOwnership(selectedOrg.id, newOwnerId);
      setTransferDialogOpen(false);
      showSnackbar('Ownership transferred successfully', 'success');
      loadOrganizations(); // Reload to get updated data
    } catch (error) {
      showSnackbar('Failed to transfer ownership', 'error');
    } finally {
      setLoading(false);
    }
  };



  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      description: '',
      domain: '',
      industry: '',
      size: '',
    });
  };

  const resetEditForm = () => {
    setEditForm({});
  };

  const resetInviteForm = () => {
    setInviteForm({
      email: '',
      role: OrganizationRole.MEMBER,  // Role they will have after accepting
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOrgSelect = (org: Organization) => {
    setSelectedOrg(org);
    loadOrganizationDetails(org.id);
  };

  const getCurrentUserRole = (): OrganizationRole | string | number => {
    if (!selectedOrg) return OrganizationRole.VIEWER;
    
    // First try to get role from the organization's user_role field
    if (selectedOrg.user_role) {
      // Convert string role to enum value (now expecting numeric string)
      const roleValue = parseInt(selectedOrg.user_role);
      if (!isNaN(roleValue)) {
        return roleValue;
      }
      
      // Fallback for legacy string role names
      const roleString = selectedOrg.user_role.toUpperCase();
      switch (roleString) {
        case 'OWNER':
          return OrganizationRole.OWNER;
        case 'ADMIN':
          return OrganizationRole.ADMIN;
        case 'MEMBER':
          return OrganizationRole.MEMBER;
        case 'VIEWER':
          return OrganizationRole.VIEWER;
        default:
          console.warn(`Unknown role value: ${selectedOrg.user_role}`);
      }
    }
    
    // Fallback to finding in members array
    if (members.length > 0) {
      const currentUserId = localStorage.getItem('gamora_user_id');
      const currentMember = members.find(member => member.user_id === currentUserId);
      if (currentMember) {
        return currentMember.role;
      }
    }
    
    // If still no role found, return VIEWER as default
    return OrganizationRole.VIEWER;
  };

  const currentUserRole = getCurrentUserRole();

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        sx={{
          fontSize: '1.1rem',
          fontWeight: 650,
          color: '#111827',
          letterSpacing: '-0.02em',
        }}
        gutterBottom
      >
        Organization Management
      </Typography>

      {/* Standalone Pending Invitations Section */}
      {invitationsLoading ? (
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f8f9fb', border: '1px solid #eceef1', borderRadius: '12px', textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading pending invitations...
          </Typography>
        </Box>
      ) : myInvitations.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#111827' }}>
                Pending Invitations ({myInvitations.length})
              </Typography>
              {invitationsLastUpdated && (
                <Typography variant="caption" color="text.secondary">
                  Last updated: {invitationsLastUpdated.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => loadMyInvitations(true)}
              disabled={invitationsLoading}
              startIcon={invitationsLoading ? <CircularProgress size={16} /> : <MailIcon />}
              sx={{
                borderColor: '#e5e7eb',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.78rem',
                color: '#6b7280',
              }}
            >
              {invitationsLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          <PendingInvitations
            invitations={myInvitations}
            onInvitationAccepted={() => {
              loadMyInvitations(false);
              loadOrganizations();
            }}
            onInvitationDeclined={() => {
              loadMyInvitations(false);
            }}
            useExternalData={true}
          />
        </Box>
      ) : (
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f8f9fb', border: '1px solid #eceef1', borderRadius: '12px', textAlign: 'center' }}>
          <MailIcon sx={{ fontSize: 36, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Pending Invitations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have any pending organization invitations at the moment.
          </Typography>
        </Box>
      )}

      {/* Organization Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#111827' }} gutterBottom>
          Select Organization
        </Typography>
        {organizations.length === 0 && myInvitations.length > 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
            You don't have any organizations yet, but you have pending invitations above. 
            Accept an invitation to join an existing organization, or create your own organization below.
          </Alert>
        )}
        {organizations.length === 0 && myInvitations.length === 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
            You don't have any organizations yet. Create your first organization below to get started.
          </Alert>
        )}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2 
        }}>
          {organizations.map((org) => (
            <Box key={org.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: selectedOrg?.id === org.id ? '2px solid #111827' : '1px solid #eceef1',
                  borderRadius: '12px',
                  boxShadow: 'none',
                }}
                onClick={() => handleOrgSelect(org)}
              >
                <CardContent>
                  <Typography variant="h6">{org.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {org.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {org.domain} • {org.industry}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
          <Box>
            <Card sx={{ cursor: 'pointer', border: '2px dashed', borderColor: '#e5e7eb', borderRadius: '12px', boxShadow: 'none' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Create New Organization
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Organization Details Tabs */}
      {selectedOrg && (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: '#eceef1' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': { fontSize: '0.8rem', textTransform: 'none' },
                '& .Mui-selected': { color: '#111827' },
                '& .MuiTab-iconWrapper': { fontSize: 18 },
              }}
            >
              <Tab label="Overview" icon={<BusinessIcon />} />
              <Tab label="Members" icon={<PeopleIcon />} />
              <Tab label="Invitations" icon={<PersonAddIcon />} />
              {myInvitations.length > 0 && (
                <Tab 
                  label={invitationsLoading ? `My Invitations (${myInvitations.length})...` : `My Invitations (${myInvitations.length})`} 
                  icon={invitationsLoading ? <CircularProgress size={16} /> : <MailIcon />} 
                />
              )}
              <Tab label="Settings" icon={<SettingsIcon />} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <OrganizationOverview 
              organization={selectedOrg}
              members={members}
              onEdit={() => setEditDialogOpen(true)}
              onDelete={() => setDeleteDialogOpen(true)}
              canEdit={canUpdateOrganization(currentUserRole)}
              canDelete={canDeleteOrganization(currentUserRole)}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <MemberManagement
              members={members}
              onRemoveMember={handleRemoveMember}
              onUpdateRole={(member) => {
                setSelectedMember(member);
                setNewRole(member.role);
                setRoleUpdateDialogOpen(true);
              }}
              canManage={canManageMembers(currentUserRole)}
              canView={canViewMembers(currentUserRole)}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <InvitationManagement
              invitations={invitations}
              onCreateInvitation={() => setInviteDialogOpen(true)}
              canManage={canManageMembers(currentUserRole)}
              canView={canViewInvitations(currentUserRole)}
            />
          </TabPanel>

          {myInvitations.length > 0 && (
            <TabPanel value={tabValue} index={3}>
              <PendingInvitations
                invitations={myInvitations}
                onInvitationAccepted={() => {
                  loadMyInvitations(false);
                  loadOrganizations();
                }}
                onInvitationDeclined={() => {
                  loadMyInvitations(false);
                }}
                useExternalData={true}
              />
            </TabPanel>
          )}

          <TabPanel value={tabValue} index={myInvitations.length > 0 ? 4 : 3}>
            <OrganizationSettings
              organization={selectedOrg}
              members={members}
              onTransferOwnership={() => setTransferDialogOpen(true)}
              canTransfer={isOwner(currentUserRole)}
            />
          </TabPanel>
        </Box>
      )}

      {/* Create Organization Dialog */}
      <CreateOrganizationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateOrganization}
        form={createForm}
        setForm={setCreateForm}
        loading={loading}
      />

      {/* Edit Organization Dialog */}
      <EditOrganizationDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSubmit={handleUpdateOrganization}
        organization={selectedOrg}
        form={editForm}
        setForm={setEditForm}
        loading={loading}
      />

      {/* Delete Organization Dialog */}
      <DeleteOrganizationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteOrganization}
        organization={selectedOrg}
        loading={loading}
      />

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSubmit={handleCreateInvitation}
        form={inviteForm}
        setForm={setInviteForm}
        loading={loading}
      />

      {/* Update Member Role Dialog */}
      <UpdateMemberRoleDialog
        open={roleUpdateDialogOpen}
        onClose={() => setRoleUpdateDialogOpen(false)}
        onSubmit={handleUpdateMemberRole}
        member={selectedMember}
        newRole={newRole}
        setNewRole={setNewRole}
        loading={loading}
      />

      {/* Transfer Ownership Dialog */}
      <TransferOwnershipDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        onSubmit={handleTransferOwnership}
        members={members}
        newOwnerId={newOwnerId}
        setNewOwnerId={setNewOwnerId}
        loading={loading}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Action Button */}
      <Tooltip title="Create Organization">
        <Fab
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 80,
            bgcolor: '#111827',
            color: 'white',
            borderRadius: '14px',
            width: 44,
            height: 44,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': { bgcolor: '#374151' },
          }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
}

export default OrganizationManagement;
